/**
 * Svelte action: horizontal auto-scroll (marquee) for overflowing single-line text.
 * The horizontal sibling of autoScrollY — identical SPEED, PAUSE, easing and shared
 * group coordination, so the two animate with the same duration and timings.
 * Animates scrollLeft; the node must be `overflow:hidden` with `white-space:nowrap`
 * so its content is wider than the box (otherwise it stays inert).
 *
 * Usage: <p use:autoScrollX={{ group: 'items-x', enabled: $isMobile }}>…</p>
 *
 * `enabled: false` keeps the action fully inert (never activates, never mutates the
 * node's style) so desktop rendering stays byte-for-byte identical.
 *
 * @param {HTMLElement} node
 * @param {Object} [params]
 * @param {string} [params.group='default-x']
 * @param {boolean} [params.enabled=true]
 */

// --- Shared coordinator (axis-agnostic; mirrors autoScrollY) ---
const groups = new Map();

const SPEED = 15; // px/sec — matches autoScrollY
const PAUSE_MS = 3500; // pause at each end — matches autoScrollY
const MIN_OVERFLOW = 5; // px — ignore tiny overflow

function getGroup(name) {
    if (!groups.has(name)) {
        groups.set(name, { instances: new Set(), timers: [], running: false });
    }
    return groups.get(name);
}

function clearGroupTimers(group) {
    group.timers.forEach(clearTimeout);
    group.timers = [];
}

function joinGroup(instance, groupName) {
    const group = getGroup(groupName);
    group.instances.add(instance);
    if (!group.running) {
        group.running = true;
        scheduleGroupCycle(group);
    }
}

function leaveGroup(instance, groupName) {
    if (!groups.has(groupName)) return;
    const group = groups.get(groupName);
    group.instances.delete(instance);
    if (group.instances.size === 0) {
        clearGroupTimers(group);
        group.running = false;
        groups.delete(groupName);
    }
}

function scheduleGroupCycle(group) {
    const t = setTimeout(() => runGroupCycle(group), PAUSE_MS);
    group.timers.push(t);
}

function runGroupCycle(group) {
    if (group.instances.size === 0) {
        group.running = false;
        return;
    }

    let maxOverflow = 0;
    for (const inst of group.instances) {
        if (!inst.isPaused) {
            maxOverflow = Math.max(maxOverflow, inst.overflow);
        }
    }

    if (maxOverflow <= 0) {
        scheduleGroupCycle(group);
        return;
    }

    const scrollMs = (maxOverflow / SPEED) * 1000;

    for (const inst of group.instances) {
        inst.scrollToEnd(scrollMs);
    }

    const t1 = setTimeout(() => {
        for (const inst of group.instances) {
            inst.scrollToStart(scrollMs);
        }
        const t2 = setTimeout(() => runGroupCycle(group), scrollMs + PAUSE_MS);
        group.timers.push(t2);
    }, scrollMs + PAUSE_MS);
    group.timers.push(t1);
}

function easeInOut(t) {
    return 0.5 - 0.5 * Math.cos(Math.PI * t);
}

// --- Action ---
export function autoScrollX(node, params = {}) {
    let groupName = params?.group || 'default-x';
    let enabled = params?.enabled !== false;

    let isActive = false;
    let isPaused = false;
    let overflow = 0;
    let originalJustifyContent = '';
    let animFrameId = null;

    const instance = {
        get isPaused() { return isPaused; },
        get overflow() { return overflow; },
        scrollToEnd(durationMs) {
            if (isPaused || overflow <= 0) return;
            animateScrollLeft(overflow, durationMs);
        },
        scrollToStart(durationMs) {
            if (isPaused) return;
            animateScrollLeft(0, durationMs);
        },
    };

    function animateScrollLeft(target, durationMs) {
        if (animFrameId) cancelAnimationFrame(animFrameId);
        const start = node.scrollLeft;
        const distance = target - start;
        if (Math.abs(distance) < 1) return;
        const startTime = performance.now();

        function step(now) {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / durationMs, 1);
            node.scrollLeft = start + distance * easeInOut(progress);
            if (progress < 1) {
                animFrameId = requestAnimationFrame(step);
            } else {
                animFrameId = null;
            }
        }
        animFrameId = requestAnimationFrame(step);
    }

    function activate() {
        if (isActive) return;
        isActive = true;
        // Pin content to the left so scrollLeft starts from the beginning.
        originalJustifyContent = node.style.justifyContent;
        node.style.justifyContent = 'flex-start';
        node.style.overflow = 'hidden';
        node.scrollLeft = 0;
        overflow = Math.max(0, node.scrollWidth - node.clientWidth);
        joinGroup(instance, groupName);
    }

    function deactivate() {
        if (!isActive) return;
        leaveGroup(instance, groupName);
        isActive = false;
        isPaused = false;
        overflow = 0;
        if (animFrameId) {
            cancelAnimationFrame(animFrameId);
            animFrameId = null;
        }
        node.style.justifyContent = originalJustifyContent;
        node.scrollLeft = 0;
    }

    function checkOverflow() {
        if (!enabled) {
            if (isActive) deactivate();
            return;
        }
        const measured = node.scrollWidth - node.clientWidth;
        if (measured > MIN_OVERFLOW) {
            overflow = measured;
            if (!isActive) activate();
        } else {
            overflow = 0;
            if (isActive) deactivate();
        }
    }

    function pause() {
        if (!isActive || isPaused) return;
        isPaused = true;
        if (animFrameId) {
            cancelAnimationFrame(animFrameId);
            animFrameId = null;
        }
    }

    function resume() {
        if (!isActive || !isPaused) return;
        isPaused = false;
    }

    node.addEventListener('mouseenter', pause);
    node.addEventListener('mouseleave', resume);
    node.addEventListener('touchstart', pause, { passive: true });
    node.addEventListener('touchend', resume);
    // iOS fires touchcancel (not touchend) when the system claims the gesture;
    // without this the marquee stays paused forever, stranding text mid-scroll.
    node.addEventListener('touchcancel', resume);

    // ResizeObserver is unavailable on some old/embedded WebViews; degrade gracefully
    // (initial check + MutationObserver still run) instead of throwing during render.
    const resizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => checkOverflow())
        : null;
    resizeObserver?.observe(node);

    const mutationObserver = new MutationObserver(() => {
        if (isActive) deactivate();
        checkOverflow();
    });
    mutationObserver.observe(node, { childList: true, characterData: true, subtree: true });

    checkOverflow();

    return {
        update(next = {}) {
            const nextEnabled = next?.enabled !== false;
            const nextGroup = next?.group || 'default-x';
            if (nextGroup !== groupName) {
                if (isActive) deactivate();
                groupName = nextGroup;
            }
            if (nextEnabled !== enabled) {
                enabled = nextEnabled;
                checkOverflow();
            }
        },
        destroy() {
            if (isActive) leaveGroup(instance, groupName);
            if (animFrameId) cancelAnimationFrame(animFrameId);
            resizeObserver?.disconnect();
            mutationObserver.disconnect();
            node.removeEventListener('mouseenter', pause);
            node.removeEventListener('mouseleave', resume);
            node.removeEventListener('touchstart', pause);
            node.removeEventListener('touchend', resume);
            node.removeEventListener('touchcancel', resume);
            node.style.justifyContent = originalJustifyContent;
            node.scrollLeft = 0;
        }
    };
}
